import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiEmpty, uiGet, uiKpis, uiMoney, uiPill, uiTrendChart } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Global Holdings → 🏢 Companies you own (backend/holdings.py). The other half of
 * the tab, 🌍 Countries, is views/countries.js, which calls ghCompanies().
 *
 * Every company you own a piece of: bought outright in M&A, stakes from
 * Corporate Investments (listed shares, startups from rounds and pitches),
 * startups won in the Tank. Top: your industries, each with its moves for all
 * the companies in it (summit, shared services, cross-sell). Left: the
 * companies. Right: the open company: what you own, the business, its value
 * line, who owns it, its industry playbook (three sector moves), and every
 * move it already had (owner's desk, board strategy, founder help, buy more,
 * sell; or grow / rebrand / sell for an acquisition).
 * Opened from elsewhere with window.__ghOpen = "ci:12" | "ma:3".
 */

const ghM = (v) => uiMoney(v, { compact: true });
let ghSel = null;
let ghFilter = "all";
const GH_FILTERS = [["all", "All"], ["acquired", "Bought in M&A"], ["startup", "Startups"], ["listed", "Listed shares"]];
const GH_LEVEL_TONE = { "Sole owner": "good", Control: "good", "Board seat": "info", Investor: "" };

async function ghExec(type, params) {
  try { toast((await postJSON("/actions/execute", { action: { type, params } })).message); return true; }
  catch (e) { toast(e.message, true); return false; }
}

const ghPct = (v) => (v >= 99.99 ? "100%" : v < 0.01 ? `${v.toFixed(4)}%` : `${v.toFixed(v < 10 ? 2 : 1)}%`);
const ghSigned = (v) => `${v >= 0 ? "+" : ""}${ghM(v)}`;

function ghStat(label, value, tone = "", sub = "") {
  return `<div class="cp-stat"><span>${esc(label)}</span><b class="${tone}">${value}</b>${sub ? `<em>${sub}</em>` : ""}</div>`;
}

function ghRow(r, on) {
  return `<button type="button" class="cp-row gh-row ${on ? "on" : ""}" data-gh="${esc(r.id)}">
    <span class="cp-row-name"><b>${esc(r.name)}</b><em>${esc(r.origin)} · ${esc(r.sector)}</em></span>
    <span class="cp-row-num"><em>Your share</em><b>${ghM(r.value)}</b></span>
    <span class="cp-row-num"><em>Gain</em><b class="${r.gain >= 0 ? "pos" : "neg"}">${ghSigned(r.gain)}</b></span>
    <span class="cp-row-num"><em>You own</em><b>${ghPct(r.pct)}</b></span>
    ${uiPill(r.level, GH_LEVEL_TONE[r.level])}</button>`;
}

function ghSector(s, home) {
  return `<article class="gh-sector ${s.fit ? "fit" : ""}">
    <div class="gh-sector-top"><b>${esc(s.sector)}</b>${s.sector === home ? uiPill("your industry", "good") : s.fit ? uiPill("next to yours", "info") : ""}</div>
    <p class="gh-sector-sub">${s.companies.length} compan${s.companies.length === 1 ? "y" : "ies"} · worth ${ghM(s.value)} · ${esc(s.companies.join(", "))}</p>
    ${s.playbook.length ? `<p class="gh-sector-sub">Playbook: ${s.playbook.map((p) => `${p.icon} ${esc(p.label)}`).join(" · ")}</p>` : ""}
    <div class="gh-sector-moves">${s.moves.map((m) => `<button type="button" data-ind="${m.key}" data-sector="${esc(s.sector)}" ${m.ready ? "" : "disabled"} title="${esc(m.what)}">
      <b>${m.icon} ${esc(m.label)}</b><span>${m.ready ? ghM(m.cost) : esc(m.why)}</span></button>`).join("")}</div></article>`;
}

function ghPlaybook(d) {
  if (!d.playbook.length) return "";
  return `<h4 class="cp-h">${esc(d.sector)} playbook <span>moves only this industry has · once every 6 months each</span></h4>
    <div class="cp-moves">${d.playbook.map((m) => `<div class="cp-move gh-play ${m.ready ? "" : "locked"}">
      <b>${m.icon} ${esc(m.label)}</b><p>${esc(m.what)}</p>
      <div class="gh-play-facts"><span>${m.effects.map(esc).join(" · ")}</span><span>${Math.round(m.chance * 100)}% it lands · ${m.need === "control" ? "needs control" : "needs a board seat"}</span></div>
      <button type="button" class="${m.ready && m.affordable ? "primary" : ""}" data-play="${m.key}" ${m.ready && m.affordable ? "" : "disabled"}>${m.ready ? (m.affordable ? `Do it · ${ghM(m.cost)}` : `Costs ${ghM(m.cost)}`) : esc(m.why)}</button></div>`).join("")}</div>`;
}

/**
 * Buying more, by percent of the company: the slider picks the %, the cost shows below.
 * A secondary is priced off the sellers' valuation (up to their max %); listed shares off
 * the market value (up to just under the 30% takeover line). Fees: 0.5% (FEE_PCT).
 */
const GH_FEE = 0.005;
function ghBuyTerms(d) {
  const listed = d.kind === "listed";
  const val = listed ? d.facts.market_cap : d.secondary.price_valuation;
  const minPct = (100 * (listed ? 10000 : 50000)) / val;
  const capPct = listed ? Math.max(0, 29.99 - d.pct) : d.secondary.max_pct;
  const cashPct = (100 * Math.max(0, d.cash) * 0.98) / (val * (1 + GH_FEE));
  const maxPct = Math.min(capPct, cashPct);
  const step = maxPct >= 5 ? 0.1 : maxPct >= 0.5 ? 0.01 : 0.0001;
  return { val, minPct: Math.ceil(minPct / step) * step, maxPct: Math.floor(maxPct / step) * step, step, capPct, listed };
}

function ghBuySlider(d) {
  const t = ghBuyTerms(d);
  if (t.maxPct < t.minPct) {
    return `<p class="iv-warn">${t.capPct < t.minPct ? "You can't buy more this way right now." : `The smallest purchase costs ${ghM((t.minPct / 100) * t.val)}; you have ${ghM(d.cash)}.`}</p>`;
  }
  const start = Math.min(t.maxPct, Math.max(t.minPct, t.listed ? t.minPct * 10 : 1));
  return `<label class="iv-slide">How much of ${esc(d.name)} to buy <b id="gh-buy-pct-out"></b>
      <input type="range" id="gh-buy-pct" min="${t.minPct}" max="${t.maxPct}" step="${t.step}" value="${start}"></label>
    <div class="gh-buy-cost" id="gh-buy-cost"></div>
    <button type="button" class="primary" data-do="buy">Buy</button>`;
}

function ghStakeMoves(d) {
  const you = d.you;
  const help = (d.help || []).length ? `<div class="cp-move"><b>🤝 Help the founders</b><p>Open doors or lend them a leader: they grow faster.</p>
    ${d.help.map((h) => `<button type="button" data-help="${h.kind}" ${h.ready ? "" : "disabled"} title="${esc(h.what)}">${esc(h.label)}${h.cost ? ` (${ghM(h.cost)})` : ""}${h.ready ? "" : " · done this quarter"}</button>`).join("")}</div>` : "";
  const board = you && you.board_seat ? `<div class="cp-move"><b>🪑 Your board seat</b><p>Steer what the company chases.</p>
    <div class="gh-strat">${["growth", "support", "profit"].map((k) => `<button type="button" class="${you.strategy === k ? "primary" : ""}" data-strat="${k}">${{ growth: "🚀 Growth", support: "🤝 Back management", profit: "💰 Profit" }[k]}</button>`).join("")}</div></div>` : "";
  const sellMove = d.moves.find((m) => m.key === "sell");
  const early = d.moves.find((m) => m.key === "early_exit");
  const canBuy = d.kind === "listed" || d.secondary || d.round;
  const buy = canBuy ? `<div class="cp-move"><b>➕ Own more of it</b><p>${d.kind === "listed" ? `Buy shares at $${d.facts.price.toFixed(2)}.` : d.secondary ? `${esc(d.secondary.seller)} will sell up to ${d.secondary.max_pct}% at a ${d.secondary.premium_pct}% premium.` : `A ${esc(d.round.name)} round is open: bid for it in Corporate Investments.`}</p>
    ${d.kind === "listed" || d.secondary ? ghBuySlider(d) : `<button type="button" data-do="to-ci">Go to the bidding</button>`}</div>` : "";
  const sell = sellMove ? `<div class="cp-move danger"><b>💸 Sell</b><p>${sellMove.disabled ? esc(sellMove.why) : "Sell part or all of your stake at today's value."}</p>
    ${sellMove.disabled ? (early ? `<button type="button" class="danger" data-do="early">${esc(early.label)}</button>` : "")
      : `<label class="iv-slide"><b data-amt="gh-sell"></b><input type="range" data-slide="gh-sell" data-pct="1" min="10" max="100" step="5" value="100"></label><button type="button" class="danger" data-do="sell">Sell</button>`}</div>` : "";
  const tiers = [...new Set((d.owner || []).map((m) => m.need))];
  const desk = tiers.length ? `<h4 class="cp-h">Owner's desk <span>you own ${ghPct(you.pct)}: the bigger the stake, the more you can do</span></h4>
    <div class="ivd-own">${tiers.map((need) => {
      const ms = d.owner.filter((m) => m.need === need), have = you.pct + 1e-6 >= need;
      return `<div class="ivd-own-tier ${have ? "on" : ""}"><div class="ivd-own-head"><b>${have ? "✓" : "🔒"} ${esc(ms[0].tier)}</b><em>${need}%+</em></div>
        ${ms.map((m) => `<button type="button" data-owner="${m.key}" ${m.ready ? "" : "disabled"} title="${esc(m.what)}"><b>${esc(m.label)}</b><span>${esc(m.what)}</span>${m.ready ? "" : `<i>${esc(m.why)}</i>`}</button>`).join("")}</div>`;
    }).join("")}</div>
    <div id="gh-owner-panel"></div>` : "";
  return `<h4 class="cp-h">Run your stake</h4><div class="cp-moves">${board}${help}${buy}${sell}</div>${desk}`;
}

function ghMaMoves(d) {
  const o = d.ma;
  const max = Math.max(50000, Math.round(Math.min(d.cash * 0.9, Math.max(d.revenue, 500000)) / 10000) * 10000);
  return `<h4 class="cp-h">Run the business</h4><div class="cp-moves">
    <div class="cp-move"><b>📈 Invest to grow it</b><p>Sales people, marketing, capacity: every 4% of a year's revenue adds about +1 pt of growth a month for 12 months.</p>
      <label class="iv-slide"><b data-amt="gh-grow"></b><input type="range" data-slide="gh-grow" min="50000" max="${max}" step="10000" value="${Math.min(max, Math.round((d.revenue * 0.04) / 10000) * 10000 || 50000)}"></label>
      <button type="button" class="primary" data-do="grow">Invest</button></div>
    <div class="cp-move"><b>🏷 Put your brand on it</b><p>One-off: +6% revenue on the line and +2 brand.</p>
      <button type="button" data-do="rebrand" ${o.rebranded ? "disabled" : ""}>${o.rebranded ? "Carries your brand" : `Rebrand (${ghM(o.rebrand_cost)})`}</button></div>
    <div class="cp-move danger"><b>💸 Sell it</b><p>This quarter's bids. ${o.quick_flip ? "Bought under 6 months ago: buyers knock 10% off. " : ""}Its ${o.employees} people and revenue leave with it.</p>
      ${o.buyers.map((b, i) => `<button type="button" class="${i === 0 ? "danger" : ""}" data-sell-to="${esc(b.buyer)}">${esc(b.buyer)} · ${ghM(b.price)} <span class="${b.price - o.paid >= 0 ? "pos" : "neg"}">(${ghSigned(b.price - o.paid)})</span></button>`).join("")}</div>
  </div>`;
}

function ghDetail(d) {
  const owners = (d.owners || []).filter((o) => o.pct > 0);
  const hist = d.history || [];
  const chartLabel = d.kind === "listed" ? "Share price" : d.kind === "startup" ? "Valuation" : "Revenue a year";
  const business = d.source === "ma"
    ? [ghStat("Brings in", `${ghM(d.revenue)}/yr`, d.revenue >= d.ma.revenue_at_purchase * 0.85 ? "" : "neg", `${ghM(d.ma.revenue_at_purchase)} when you bought it`),
      ghStat("Growth", `${d.growth_pct}%/mo`, d.growth_pct >= 0 ? "pos" : "neg", d.ma.boost_months ? `+${d.ma.boost_pts} pts boost, ${d.ma.boost_months} mo left` : ""),
      ghStat("Customer satisfaction", d.satisfaction === null ? "—" : `${Math.round(d.satisfaction)}/100`),
      ghStat("People", String(d.ma.employees), "", `runs as “${esc(d.ma.line)}”`)]
    : d.kind === "listed"
      ? [ghStat("Share price", `$${d.facts.price.toFixed(2)}`), ghStat("Market value", ghM(d.facts.market_cap)),
        ghStat("Revenue", `${ghM(d.revenue)}/yr`), ghStat("Dividend", `${d.facts.yield_pct}%`, "", "a year")]
      : [ghStat("Valuation", ghM(d.facts.valuation), "", esc(d.stage)), ghStat("Revenue", `${ghM(d.revenue)}/yr`),
        ghStat("Growth · margin", `${d.facts.growth_pct}% · ${d.facts.margin_pct}%`, d.facts.margin_pct < 0 ? "neg" : "", "a month"),
        ghStat("Cash in the company", ghM(d.facts.cash), "", `founder ${esc(d.facts.founder)}`)];
  return `
    <div class="cp-head"><div><span class="sx-eyebrow">${esc(d.origin)} · ${esc(d.sector)} · ${esc(d.country)}</span><h3>${esc(d.name)}</h3></div>
      <div class="cp-head-pills">${d.ma && d.ma.integrating ? uiPill("merging", "info") : ""}${uiPill(d.level, GH_LEVEL_TONE[d.level])}${uiPill(d.gain >= 0 ? "worth more than you paid" : "worth less than you paid", d.gain >= 0 ? "good" : "bad")}</div></div>

    <h4 class="cp-h">What you own</h4>
    <div class="cp-stats">
      ${ghStat("You own", ghPct(d.pct), "", `for ${d.months_owned} month${d.months_owned === 1 ? "" : "s"}`)}
      ${ghStat("Your share is worth", ghM(d.value), "", d.pct < 99.99 ? `of ${ghM(d.company_value)} in all` : "")}
      ${ghStat("You put in", ghM(d.cost))}
      ${ghStat("Gain", ghSigned(d.gain), d.gain >= 0 ? "pos" : "neg", `${d.gain_pct}%`)}
    </div>
    ${owners.length > 1 ? `<div class="cp-split">${owners.map((o, i) => `<i style="flex:${Math.max(0.5, o.pct)};background:${o.who === "You" ? "var(--brass)" : ["#7ee8fa", "#a78bfa", "#94a3b8"][i % 3]}" title="${esc(o.who)} ${o.pct}%"></i>`).join("")}</div>
      <div class="cp-legend">${owners.map((o, i) => `<span><i style="background:${o.who === "You" ? "var(--brass)" : ["#7ee8fa", "#a78bfa", "#94a3b8"][i % 3]}"></i>${esc(o.who)} <b>${o.pct}%</b></span>`).join("")}</div>` : ""}
    ${(d.rights || []).length ? `<div class="cp-tags">${d.rights.map((r) => `<span class="${r.have ? "" : "dim"}" title="${esc(r.what)}">${r.have ? "✓" : "○"} ${esc(r.label)} (${r.at}%)</span>`).join("")}</div>` : ""}

    <h4 class="cp-h">The business</h4>
    <div class="cp-stats">${business.join("")}</div>
    ${hist.length > 1 ? `<div class="gh-chart"><span>${chartLabel}</span>${uiTrendChart(hist.map((p) => p[1]), { labels: hist.map((p) => `M${p[0]}`), money: d.kind !== "listed" })}</div>` : ""}
    <div class="cp-tags">${d.fit ? `<span>🧩 Fits your industry: playbook moves land more often</span>` : ""}
      ${d.operate_here ? `<button type="button" class="gh-link" data-country="${esc(d.country)}">🌍 You operate in ${esc(d.country)}: open it in Countries</button>` : ""}</div>

    ${ghPlaybook(d)}
    ${d.source === "ma" ? ghMaMoves(d) : ghStakeMoves(d)}

    ${[...(d.log || []), ...(d.feed || [])].length ? `<h4 class="cp-h">What's happened</h4><ul class="iv-feed">${[...(d.log || []), ...(d.feed || [])].slice(0, 14).map((f) => `<li><i>M${f.month}</i>${esc(f.text)}</li>`).join("")}</ul>` : ""}`;
}

function ghWireDetail(host, d) {
  const done = async (ok) => { if (ok) await window.refreshAll(); return ok; };
  host.querySelectorAll("[data-slide]").forEach((r) => {
    const out = host.querySelector(`[data-amt="${r.dataset.slide}"]`);
    const paint = () => { out.textContent = r.dataset.pct ? `${r.value}% of your stake` : ghM(+r.value); };
    r.addEventListener("input", paint); paint();
  });
  const val = (k) => +host.querySelector(`[data-slide="${k}"]`).value;
  const buyPct = host.querySelector("#gh-buy-pct");
  const buyAmount = () => Math.round((+buyPct.value / 100) * ghBuyTerms(d).val);
  if (buyPct) {
    const t = ghBuyTerms(d);
    const dp = t.step < 0.01 ? 4 : 2;
    const paint = () => {
      const pct = +buyPct.value, cost = buyAmount();
      host.querySelector("#gh-buy-pct-out").textContent = `${pct.toFixed(dp)}% of the company`;
      host.querySelector("#gh-buy-cost").innerHTML = `<div><em>Cost</em><b>${ghM(cost)}</b></div><div><em>Fee (0.5%)</em><b>${ghM(cost * GH_FEE)}</b></div>
        <div><em>You'd own</em><b>${ghPct(d.pct + pct)}</b></div><div><em>Cash left</em><b>${ghM(d.cash - cost * (1 + GH_FEE))}</b></div>`;
    };
    buyPct.addEventListener("input", paint); paint();
  }
  const busy = (b) => { b.disabled = true; return () => { b.disabled = false; }; };
  host.querySelectorAll("[data-play]").forEach((b) => b.addEventListener("click", async () => {
    const m = d.playbook.find((x) => x.key === b.dataset.play);
    if (!confirm(`${m.label} at ${d.name} for ${ghM(m.cost)}?\n\n${m.what}\n${Math.round(m.chance * 100)}% it lands in full.`)) return;
    const undo = busy(b);
    if (!(await done(await ghExec("hold_sector_move", { id: d.id, move: m.key })))) undo();
  }));
  host.querySelectorAll("[data-help]").forEach((b) => b.addEventListener("click", async () => {
    const undo = busy(b);
    if (!(await done(await ghExec("ci_help", { company_id: d.ref, kind: b.dataset.help })))) undo();
  }));
  host.querySelectorAll("[data-strat]").forEach((b) => b.addEventListener("click", async () => {
    await done(await ghExec("ci_strategy", { company_id: d.ref, strategy: b.dataset.strat }));
  }));
  host.querySelectorAll("[data-sell-to]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm(`Sell ${d.name} to ${b.dataset.sellTo}?`)) return;
    const undo = busy(b);
    if (await done(await ghExec("ma_owned_sell", { acq_id: d.ref, buyer: b.dataset.sellTo }))) ghSel = null; else undo();
  }));
  host.querySelectorAll("[data-country]").forEach((b) => b.addEventListener("click", () => {
    window.__cpSelect = b.dataset.country;
    window.__ghSeg = "countries";
    window.renderCountries(host.closest(".gh-root").parentElement);
  }));
  host.querySelectorAll("[data-owner]").forEach((b) => b.addEventListener("click", async () => {
    const m = d.owner.find((x) => x.key === b.dataset.owner);
    if (m.key === "inject") {
      const panel = host.querySelector("#gh-owner-panel");
      const maxAmt = Math.max(100000, Math.round((d.cash * 0.9) / 50000) * 50000);
      panel.innerHTML = `<p class="bk-sub">${esc(m.what)}</p>
        <label class="iv-slide">How much to put in <b id="gh-inj-amt"></b><input type="range" id="gh-inj" min="100000" max="${maxAmt}" step="50000" value="${Math.min(maxAmt, 1000000)}"></label>
        <div class="cp-go"><button type="button" class="primary" id="gh-inj-go">Invest</button></div>`;
      const r = panel.querySelector("#gh-inj");
      const paint = () => { panel.querySelector("#gh-inj-amt").textContent = `${ghM(+r.value)} → you'd own ${((100 * (d.value + +r.value)) / (d.company_value + +r.value)).toFixed(2)}%`; };
      r.addEventListener("input", paint); paint();
      panel.querySelector("#gh-inj-go").addEventListener("click", async () => done(await ghExec("ci_owner", { company_id: d.ref, move: "inject", amount: +r.value })));
      return;
    }
    if (!confirm(`${m.label} at ${d.name}?\n\n${m.what}`)) return;
    const undo = busy(b);
    if (await done(await ghExec("ci_owner", { company_id: d.ref, move: m.key }))) { if (m.key === "sell_company") ghSel = null; } else undo();
  }));
  host.querySelectorAll("[data-do]").forEach((b) => b.addEventListener("click", async () => {
    const k = b.dataset.do;
    if (k === "to-ci") { window.ges && window.ges.showTab("investments"); return; }
    const undo = busy(b);
    let ok = false;
    if (k === "grow") ok = await ghExec("ma_owned_grow", { acq_id: d.ref, amount: val("gh-grow") });
    else if (k === "rebrand") ok = confirm(`Rebrand ${d.name} under your name for ${ghM(d.ma.rebrand_cost)}?`) && await ghExec("ma_owned_rebrand", { acq_id: d.ref });
    else if (k === "buy") ok = confirm(`Buy ${(+buyPct.value).toFixed(2)}% of ${d.name} for ${ghM(buyAmount())} (+ 0.5% fee)?`)
      && await ghExec(d.kind === "listed" ? "ci_buy_shares" : "ci_secondary", { company_id: d.ref, amount: buyAmount() });
    else if (k === "sell") ok = confirm(`Sell ${val("gh-sell")}% of your stake in ${d.name}?`) && await ghExec("ci_sell", { company_id: d.ref, pct_of_holding: val("gh-sell") });
    else if (k === "early") ok = confirm(`Sell your whole stake back to ${d.name} at a 20% discount?`) && await ghExec("ci_sell", { company_id: d.ref, pct_of_holding: 100, early: true });
    if (!ok) { undo(); return; }
    if ((k === "sell" && val("gh-sell") >= 100) || k === "early") ghSel = null;
    await window.refreshAll();
  }));
}

/** Draw the Companies half into host (a node inside the Global Holdings tab). */
window.ghCompanies = async function (host) {
  const v = await uiGet("/holdings");
  if (window.__ghOpen) { ghSel = window.__ghOpen; ghFilter = "all"; window.__ghOpen = null; }
  const list = v.companies.filter((r) => ghFilter === "all" || r.kind === ghFilter);
  if (!list.some((r) => r.id === ghSel)) ghSel = (list[0] || {}).id || null;
  const s = v.summary;
  host.innerHTML = `
    ${uiKpis([
      { label: "Companies you own", value: s.companies, sub: s.controlled ? `${s.controlled} you control` : "" },
      { label: "Your share is worth", value: ghM(s.value) },
      { label: "Gain on what you put in", value: ghSigned(s.gain), tone: s.gain >= 0 ? "pos" : "neg" },
      { label: "Their revenue (your share)", value: ghM(s.revenue), sub: "a year" },
      { label: "Industries", value: s.sectors },
      { label: "Cash", value: ghM(v.cash) },
    ])}
    ${v.companies.length ? `
      <h3 class="iv-sec-title">Your industries <span>moves for every company you own in a sector at once</span></h3>
      <div class="gh-sectors">${v.sectors.map((x) => ghSector(x, v.home_sector)).join("")}</div>
      <div class="gh-filters">${GH_FILTERS.map(([k, l]) => `<button type="button" class="${ghFilter === k ? "on" : ""}" data-ghf="${k}">${l} <span>${k === "all" ? v.companies.length : v.companies.filter((r) => r.kind === k).length}</span></button>`).join("")}</div>
      <div class="cp-desk">
        <aside class="cp-list">${list.length ? list.map((r) => ghRow(r, r.id === ghSel)).join("") : `<p class="iv-none">None of this kind.</p>`}
          <p class="cp-how">ⓘ ${esc(v.how)}</p>
          <button type="button" class="cp-new" data-go="investments">💼 Find more to invest in</button>
          <button type="button" class="cp-new" data-go="ma">🏢 Buy a company (M&amp;A)</button></aside>
        <section class="cp-read" id="gh-read"><p class="iv-thinking">Opening…</p></section>
      </div>`
    : uiEmpty("You don't own any companies yet", "Buy one outright in M&A, take a stake in Corporate Investments, or win a startup in the Tank. It shows up here, with everything you can do with it.",
      `<div class="gh-empty-go"><button type="button" class="primary" data-go="investments">💼 Corporate Investments</button><button type="button" data-go="ma">🏢 M&amp;A</button></div>`)}
    ${v.teams.length ? `<h3 class="iv-sec-title">Teams you acqui-hired <span>absorbed into your company: they live on in your people, not as companies</span></h3>
      <div class="sx-table-wrap"><table class="sx-table"><tr><th>Startup</th><th>What they brought</th><th>Led by</th><th class="num">Paid</th><th class="num">Month</th></tr>
      ${v.teams.map((t) => `<tr><td>${esc(t.startup)}</td><td>${esc(t.skill)}</td><td>${esc(t.leader)}</td><td class="num">${ghM(t.price)}</td><td class="num">M${t.month}</td></tr>`).join("")}</table></div>` : ""}
    ${v.closed.length ? `<details class="mo-sold"><summary>Companies you no longer own (${v.closed.length})</summary><table class="sx-table">
      <tr><th>Company</th><th>How it ended</th><th class="num">Put in</th><th class="num">Gain / loss</th></tr>
      ${v.closed.map((c) => `<tr><td>${esc(c.name)}</td><td>${esc(c.how)}</td><td class="num">${ghM(c.cost)}</td><td class="num ${c.result >= 0 ? "pos" : "neg"}">${ghSigned(c.result)}</td></tr>`).join("")}</table></details>` : ""}`;
  host.querySelectorAll("[data-go]").forEach((b) => b.addEventListener("click", () => window.ges && window.ges.showTab(b.dataset.go)));
  host.querySelectorAll("[data-ghf]").forEach((b) => b.addEventListener("click", () => { ghFilter = b.dataset.ghf; window.ghCompanies(host); }));
  host.querySelectorAll("[data-gh]").forEach((b) => b.addEventListener("click", () => { ghSel = b.dataset.gh; window.ghCompanies(host); }));
  host.querySelectorAll("[data-ind]").forEach((b) => b.addEventListener("click", async () => {
    const sec = v.sectors.find((x) => x.sector === b.dataset.sector), m = sec.moves.find((x) => x.key === b.dataset.ind);
    if (!confirm(`${m.label} in ${sec.sector} for ${ghM(m.cost)}?\n\n${m.what}\nIt acts on: ${sec.companies.join(", ")}.`)) return;
    b.disabled = true;
    if (await ghExec("hold_industry_move", { sector: sec.sector, move: m.key })) await window.refreshAll(); else b.disabled = false;
  }));
  const read = host.querySelector("#gh-read");
  if (!read || !ghSel) return;
  let d;
  try { d = await uiGet(`/holdings/company/${encodeURIComponent(ghSel)}`); }
  catch (e) { read.innerHTML = `<p class="iv-warn">${esc(e.message)}</p>`; return; }
  try {
    read.innerHTML = ghDetail(d);
    ghWireDetail(read, d);
  } catch (e) {
    console.error("Companies you own:", e);
    read.innerHTML = `<p class="iv-warn">Couldn't show ${esc(d.name || "")}: ${esc(e.message)}</p>`;
  }
};

export {};
